import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, Trash2, X, Eye, EyeOff } from 'lucide-react';
import Card from 'components/card';
import InputField from 'components/fields/InputField';
import SettingsMenu from 'views/admin/main/account/component/SettingsMenu';
import LoadingTableLine from './components/loadingTableLine';
import {
    createCompanyUser,
    deleteCompanyUser,
    getCompanyAccounts,
    updateCompanyUser,
} from 'interfaces/user';
import { getPermissions } from 'interfaces/user';
import { getMyInfo } from 'interfaces/user';
import { updateCompanyUserPermissions } from 'interfaces/user';
import { toast } from 'react-toastify';
import DeleteModal from 'components/actions/Delete';
import { useDisclosure } from '@chakra-ui/hooks';

interface User {
    id: string;
    fullName: string;
    email: string;
    roles: string[];
    permissions: string[];
}

interface FormData {
    fullName: string;
    email: string;
    password: string;
    passwordConfirmation: string;
    roles: string[];
    permissions: string[];
}

const Users = () => {
    const [isFullyLoaded, setIsFullyLoaded] = useState(false);
    const [users, setUsers] = useState<User[]>([]);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [roleFilter] = useState<string>('');
    const { isOpen, onClose, onOpen } = useDisclosure();
    const [permissionFilter, setPermissionFilter] = useState<string>('');
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showPasswordConfirm, setShowPasswordConfirm] = useState<boolean>(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [error, setError] = useState(false);
    const [isOwner, setIsOwner] = useState<boolean>(false);
    const [formData, setFormData] = useState<FormData>({
        fullName: '',
        email: '',
        password: '',
        passwordConfirmation: '',
        roles: [],
        permissions: []
    });

    //const availableRoles: string[] = ['admin', 'manager', 'employee'];
    const [availablePermissions, setAvailablePermissions] = useState<string[]>([]);
    const [myPermissions, setMyPermissions] = useState<string[]>([]);

    const initPermissions = async () => {
        const res = await getPermissions();
        if (res && res.data && Array.isArray(res.data)) {
            // assume API returns array of permission strings
            setAvailablePermissions(res.data);
        }
    };

    const initMyInfo = async () => {
        const res = await getMyInfo();
        if (res && res.data) {
            // assume res.data.permissions is array of strings or objects
            const perms = Array.isArray(res.data.permissions)
                ? res.data.permissions.map((p: any) => (typeof p === 'string' ? p : p.name))
                : [];
            setMyPermissions(perms);
            // Owner has no ownerId in DB. Sub-accounts have ownerId set to the owner's _id.
            setIsOwner(!res.data.ownerId);
        }
    };

    const initAccounts = async () => {
        setIsFullyLoaded(false);
        const myFullName = localStorage.getItem('name');
        const res = await getCompanyAccounts();

        if (res) {
            const filteredUsers = res.data
                .filter((user: any) => user.fullName !== myFullName)
                .map((user: any) => ({
                    id: user._id,
                    fullName: user.fullName,
                    email: user.email,
                    roles: user.roles || [],
                    permissions: user.permissions || []
                }));
            setUsers(filteredUsers);
            setIsFullyLoaded(true);
        }
    };

    const filteredUsers = users.filter(user => {
        const matchesSearch = user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             user.email.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesRole = roleFilter === '' || user.roles.includes(roleFilter);
        const matchesPermission = permissionFilter === '' //|| user.permissions.includes(permissionFilter);
        
        return matchesSearch && matchesRole && matchesPermission;
    });

    const handleSubmit = async () => {
        if (!formData.fullName || !formData.email) {
            toast.error('Nom et email sont requis');
            return;
        }

        if (!editingUser && (!formData.password || formData.password !== formData.passwordConfirmation)) {
            toast.error('Les mots de passe ne correspondent pas');
            return;
        }
        if (!isOwner && !myPermissions.includes('manage:users')) {
            toast.error('Vous n\'avez pas les droits pour modifier cet utilisateur');
            return;
        }
        try {
            let res;
            if (editingUser) {
                // Update only changed fields
                const updateData = {
                    fullName: editingUser.fullName,
                    email: editingUser.email,
                
            };
                res = await updateCompanyUser(editingUser.id, updateData);
                if (res && !res.error) {
                    toast.success('Utilisateur mis à jour avec succès');
                    initAccounts();
                } else {
                    toast.error(res?.error || 'Erreur lors de la modification');
                    return;
                }
            } else {
                res = await createCompanyUser(formData);
                if (res) {
                    toast.success('Utilisateur créé avec succès');
                    initAccounts();
                } else {
                    setError(true);
                    toast.error('Erreur lors de la création');
                    return;
                }
            }
            handleCloseModal();
        } catch (error) {
            console.error('Erreur handleSubmit:', error);
            toast.error('Erreur lors de la sauvegarde');
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingUser(null);
        setFormData({
            fullName: '',
            email: '',
            password: '',
            passwordConfirmation: '',
            roles: [],
            permissions: []
        });
        setShowPassword(false);
        setShowPasswordConfirm(false);
        setError(false);
    };

    const handleEdit = (user: User) => {
        setEditingUser(user);
        setFormData({
            fullName: user.fullName,
            email: user.email,
            password: '',
            passwordConfirmation: '',
            roles: user.roles,
            permissions: user.permissions
        });
        setShowModal(true);
    };

    const canDeleteUsers = () => {
        return isOwner || myPermissions.includes('manage:users');
    }
    const handleDeleteAccount = async (id: string) => {
        if (!canDeleteUsers()) {
            toast.error("Vous n'avez pas la permission de supprimer des utilisateurs");
            return;
        }
        onOpen();
        setUserToDelete(id);
    };

    const deleteCompanyUserAndRefresh = async () => {
        const res = await deleteCompanyUser(userToDelete);
        setUserToDelete(null);
        if (res) {
            toast.success('Utilisateur supprimé avec succès');
            initAccounts(); // Recharger la liste au lieu de window.location.reload()
        } else {
            toast.error("Erreur lors de la suppression de l'utilisateur");
        }
        onClose();
    };

    const handlePermissionChange = async (permission: string) => {
        // If editing an existing user, persist change immediately
        if (editingUser) {
            let newPermissions = formData.permissions.includes(permission)
                ? formData.permissions.filter(p => p !== permission)
                : [...formData.permissions, permission];

            const perm: any = availablePermissions.find((p: any) => p.name === permission);

            if (perm && perm.required.length > 0) {
                // Ensure all required permissions are included
                perm.required.forEach((req: string) => {
                    if (!newPermissions.includes(req)) {
                        newPermissions.push(req);
                    }
                });
            }


            // Optimistically update UI
            setFormData(prev => ({ ...prev, permissions: newPermissions }));

            // Call API to update permissions
            try {
                const res = await updateCompanyUserPermissions(editingUser.id, newPermissions);
                if (res && !res.error) {
                    toast.success('Permissions mises à jour');
                    initAccounts();
                } else {
                    toast.error("Impossible de mettre à jour les permissions");
                    // rollback optimistic update
                    setFormData(prev => ({ ...prev, permissions: editingUser.permissions }));
                }
            } catch {
                toast.error("Erreur réseau lors de la mise à jour des permissions");
                setFormData(prev => ({ ...prev, permissions: editingUser.permissions }));
            }
            return;
        }

        // For new user creation, just toggle locally
        setFormData(prev => ({
            ...prev,
            permissions: prev.permissions.includes(permission)
                ? prev.permissions.filter(p => p !== permission)
                : [...prev.permissions, permission]
        }));
    };

    useEffect(() => {
        initAccounts();
    }, []);

    useEffect(() => {
        initPermissions();
    }, []);

    useEffect(() => {
        initMyInfo();
    }, []);

    return (
        <div className="mt-2">
            <SettingsMenu path="/settings/users" />
            <DeleteModal
                    isOpen={isOpen}
                    onClose={onClose}
                    description="Êtes-vous sûr de vouloir supprimer ce compte ?"
                    context="ce compte"
                    archived={false}
                    deleteFunction={() => {deleteCompanyUserAndRefresh()}}
                />
            {/* En-tête avec nouveau design */}
            <Card extra="mt-4 pt-[10px] pl-[20px] pr-[20px] pb-[20px] border">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h4 className="text-lg font-medium text-gray-900">Gestion des utilisateurs</h4>
                    </div>
                    <button 
                        onClick={() => {
                            if (!editingUser) {
                                setFormData(prev => ({ ...prev, permissions: myPermissions }));
                            }
                            setShowModal(true);
                        }}
                        className="bg-toola-500 hover:bg-toola-600 active:bg-toola-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
                    >
                        <Plus className="w-4 h-4" />
                        Ajouter un utilisateur
                    </button>
                </div>

                {/* Filtres */}
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                    <div className="flex-1">
                        <div className="relative">
                            <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Rechercher par nom ou email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-toola-500 focus:border-transparent"
                            />
                        </div>
                    </div>
                    
                    {/* <select 
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-toola-500 focus:border-transparent"
                    >
                        <option value="">Tous les rôles</option>
                        {availableRoles.map(role => (
                            <option key={role} value={role}>{role}</option>
                        ))}
                    </select> */}

                    <select 
                        value={permissionFilter}
                        onChange={(e) => setPermissionFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-toola-500 focus:border-transparent"
                    >
                        <option value="">Toutes les permissions</option>
                        {availablePermissions.map((p: any) => (
                            <option key={p._id} value={p.name}>{p.label}</option>
                        ))}
                    </select>
                </div>

                {error && (
                    <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-600">
                            Aucun utilisateur trouvé. Vous pouvez{' '}
                            <span
                                className="hover:cursor-pointer text-toola-500 underline"
                                onClick={() =>
                                    window.open(
                                        'https://app.toola.eu/auth/register',
                                        '_blank',
                                    )
                                }
                            >
                                créer un nouveau compte
                            </span>{' '}
                            puis rajouter les permissions.
                        </p>
                    </div>
                )}

                {/* Tableau des utilisateurs */}
                {isFullyLoaded ? (
                    users.length > 0 ? (
                        <div className="overflow-y-auto">
                            <table className="min-w-full">
                                <thead>
                                    <tr className="border-b border-gray-200">
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Nom
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Email
                                        </th>
                                        {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Rôles
                                        </th> */}
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Permissions
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {filteredUsers.map((user) => (
                                        <tr key={user.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {user.fullName}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-toola-500">
                                                {user.email}
                                            </td>
                                            {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {user.roles.length > 0 ? user.roles.join(', ') : '-'}
                                            </td> */}
                                            <td className="px-6 py-4  text-xs max-w-10 text-sm text-gray-900">
                                                {user?.permissions.length > 0 ? availablePermissions.filter((p: any) => user?.permissions.includes(p.name)).map((p: any) => p.label).join(', ') : 'Pas de permissions'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                <div className="flex items-center gap-3">
                                                    <button 
                                                        onClick={() => handleEdit(user)}
                                                        className="text-toola-500 hover:text-toola-600 flex items-center gap-1"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                        Modifier
                                                    </button>
                                                    {canDeleteUsers() && (
                                                        <button 
                                                            onClick={() => handleDeleteAccount(user.id)}
                                                            className="text-red-600 hover:text-red-800 flex items-center gap-1"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                            Supprimer
                                                        </button>
                                                   )}  
                                                   {!canDeleteUsers() && (
                                                       <span className="text-gray-400 text-sm">
                                                           Pas de permission
                                                       </span>
                                                   )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="rounded-xl border bg-white/0 outline-none">
                            <p className="my-6 text-center">
                                Vous n'avez pas encore créé d'utilisateur.
                            </p>
                        </div>
                    )
                ) : (
                    <div>
                        {Array(5).fill(null).map((_, index) => (
                            <LoadingTableLine key={index} />
                        ))}
                    </div>
                )}
            </Card>

            {/* Modal de création/édition */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-[9999]">
                    <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900">
                                    {editingUser ? 'Modifier l\'utilisateur' : 'Ajouter un utilisateur'}
                                </h2>
                                {editingUser && (
                                    <button 
                                        onClick={handleCloseModal}
                                        className="text-toola-500 hover:text-toola-600 text-sm flex items-center gap-1 mt-1"
                                    >
                                        ← Retour à tous les utilisateurs
                                    </button>
                                )}
                            </div>
                            <button 
                                onClick={handleCloseModal}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <InputField
                                    label="Nom complet *"
                                    placeholder="Jean Dupont"
                                    id="fullName"
                                    type="text"
                                    value={formData.fullName}
                                    onChange={(e: any) => setFormData(prev => ({...prev, fullName: e.target.value}))}
                                />
                            </div>

                            <div>
                                <InputField
                                    label="Email *"
                                    placeholder="jean.dupont@example.com"
                                    id="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e: any) => setFormData(prev => ({...prev, email: e.target.value}))}
                                />
                            </div>

                            {!editingUser && (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Mot de passe
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                value={formData.password}
                                                onChange={(e) => setFormData(prev => ({...prev, password: e.target.value}))}
                                                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-toola-500 focus:border-transparent"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                                            >
                                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Confirmation du mot de passe
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showPasswordConfirm ? "text" : "password"}
                                                value={formData.passwordConfirmation}
                                                onChange={(e) => setFormData(prev => ({...prev, passwordConfirmation: e.target.value}))}
                                                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-toola-500 focus:border-transparent"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                                                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                                            >
                                                {showPasswordConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}

                            <div>
                                <h3 className="text-sm font-medium text-gray-700 mb-4">Permissions utilisateur</h3>
                                
                                <div className="mb-6">
                                    {/* <h4 className="text-sm font-medium text-gray-700 mb-3">Rôles</h4>
                                    <div className="grid grid-cols-3 gap-4">
                                        {availableRoles.map((role) => (
                                            <label key={role} className="flex items-center">
                                                                    <input
                                                                        type="checkbox"
                                                    checked={formData.roles.includes(role)}
                                                    onChange={() => handleRoleChange(role)}
                                                                        className="mr-2 h-4 w-4 text-toola-500 focus:ring-toola-500 border-gray-300 rounded"
                                                                    />
                                                <span className="text-sm text-gray-700">{role}</span>
                                                            </label>
                                        ))}
                                    </div> */}
                                            </div>

                                            <div>
                                    <h4 className="text-sm font-medium text-gray-700 mb-3">Permissions</h4>
                                    <div className="grid grid-cols-2 gap-4">
                                        {availablePermissions.map((p: any) => (
                                            <label key={p._id} className="flex items-center">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={formData.permissions.includes(p.name)}
                                                                    onChange={() => handlePermissionChange(p.name)}
                                                                    className="mr-2 h-4 w-4 text-toola-500 focus:ring-toola-500 border-gray-300 rounded"
                                                                />
                                                <span className="text-sm text-gray-700">{p.label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                </div>
                            </div>

                            <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSubmit}
                                    className="px-6 py-2 bg-toola-500 text-white rounded-lg hover:bg-toola-600 transition-colors"
                                >
                                    Sauvegarder
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Users;